import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { FiRefreshCcw, FiExternalLink, FiShield, FiX } from 'react-icons/fi';
import api from '../../../api';

function fmt(ts) {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString(); } catch { return String(ts); }
}

export default function OAuth2Tab({ userId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLd] = useState(true);
  const [error, setError] = useState('');
  const [revoking, setRevoking] = useState('');

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  // ✅ memoize headers so dependency equality is stable
  const headers = useMemo(() => {
    return token ? { Authorization: `Bearer ${token}` } : null;
  }, [token]);

  const load = useCallback(async () => {
    if (!headers) { setError('Not authenticated'); setLd(false); return; }
    if (!userId)  { setError('Missing userId');    setLd(false); return; }

    const ctrl = new AbortController();
    setLd(true); setError('');

    try {
      const res = await api.get('/admin/oauth/links', {
        params: { userId },
        headers,
        signal: ctrl.signal,
      });
      const arr = Array.isArray(res.data?.links) ? res.data.links : [];
      setRows(arr);
    } catch (e) {
      if (e.name === 'CanceledError' || e.code === 'ERR_CANCELED') return;
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        'Could not load linked apps.';
      setError(msg);
    } finally {
      setLd(false);
    }

    return () => ctrl.abort();
  }, [headers, userId]);

  useEffect(() => { load(); }, [load]);

  const onUnlink = async (clientId, clientName) => {
    if (!headers) return;
    if (!window.confirm(`Unlink "${clientName || clientId}" from this account?`)) return;
    const ctrl = new AbortController();
    try {
      setRevoking(clientId);
      await api.post(
        `/admin/oauth/links/${userId}/${encodeURIComponent(clientId)}/revoke`,
        null,
        { headers, signal: ctrl.signal }
      );
      await load();
    } catch (e) {
      const msg = e?.response?.data?.message || 'Failed to unlink app.';
      alert(msg);
    } finally {
      setRevoking('');
    }
  };

  return (
    <div className="bg-[#0f1725] rounded-2xl shadow-lg p-4 sm:p-6">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-yellow-400/20 text-yellow-300">
            <FiShield />
          </span>
          <div>
            <h3 className="text-white font-semibold">OAuth2 • Linked Apps</h3>
            <p className="text-xs text-gray-400">Apps that have access via OAuth (refresh tokens).</p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 rounded bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-60"
        >
          <FiRefreshCcw className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {loading && (
        <div className="py-10 text-center text-gray-300">Loading linked apps…</div>
      )}

      {!loading && error && (
        <div className="p-4 rounded bg-red-900/30 text-red-200 border border-red-800">
          {error}
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="p-4 rounded bg-gray-800 text-gray-300">
          No linked apps found for this user.
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-300 border-b border-gray-700">
                <th className="py-2 pr-4">App</th>
                <th className="py-2 pr-4">Client ID</th>
                <th className="py-2 pr-4">Active RTs</th>
                <th className="py-2 pr-4">Last Access</th>
                <th className="py-2 pr-4">Latest Issued</th>
                <th className="py-2 pr-4">Latest Expires</th>
                <th className="py-2 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.userId || 'u'}::${r.clientId}`} className="border-b border-gray-800">
                  <td className="py-3 pr-4 text-white">
                    <div className="flex items-center gap-2">
                      {r.logoUrl ? (
                        <img
                          src={r.logoUrl}
                          alt=""
                          className="w-5 h-5 rounded"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : null}
                      <span>{r.clientName || r.clientId}</span>
                    </div>
                  </td>
                  <td className="py-3 pr-4 text-gray-300">
                    <span className="font-mono">{r.clientId}</span>
                  </td>
                  <td className="py-3 pr-4">
                    <span className="inline-block px-2 py-0.5 rounded bg-yellow-300 text-black text-xs font-semibold">
                      {r.activeRefreshTokens}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-gray-300">{fmt(r.lastAccessIssuedAt)}</td>
                  <td className="py-3 pr-4 text-gray-300">{fmt(r.latestIssuedAt)}</td>
                  <td className="py-3 pr-4 text-gray-300">{fmt(r.latestExpiresAt)}</td>
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-blue-300 hover:text-blue-200"
                        title="Details"
                      >
                        <FiExternalLink /> Details
                      </button>
                      <button
                        onClick={() => onUnlink(r.clientId, r.clientName)}
                        disabled={revoking === r.clientId}
                        className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded bg-red-600 hover:bg-red-500 text-white disabled:opacity-60"
                        title="Unlink (revoke)"
                      >
                        <FiX />
                        {revoking === r.clientId ? 'Unlinking…' : 'Unlink'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-gray-500">
            Unlink revokes all <em>refresh tokens</em> for this user & app. Existing access tokens may remain valid
            until they expire.
          </p>
        </div>
      )}
    </div>
  );
}
